<template>
  <div class="page-wrapper admin-stats-sheet admin-stats-sheet--cols-4">
    <div class="stats-row">
      <div class="mini-stat" v-for="s in stats" :key="s.label">
        <div class="mini-stat-icon" :style="{ background: s.bg, color: s.color }"><component :is="s.icon" /></div>
        <div><p class="mini-stat-val">{{ s.value }}</p><p class="mini-stat-label">{{ s.label }}</p></div>
      </div>
    </div>

    <div class="filter-bar">
      <div class="search-box">
        <el-icon><Search /></el-icon>
        <input v-model="searchQuery" type="text" placeholder="搜索学员名称或手机号" />
      </div>
      <div class="filter-group">
        <el-select v-model="filterStatus" placeholder="筛选状态" style="width: 140px" @change="loadMembers">
          <el-option label="全部状态" value="" />
          <el-option label="活跃" :value="1" />
          <el-option label="已离职" :value="0" />
        </el-select>
      </div>
    </div>

    <div class="members-table">
      <div class="table-scroll">
      <table>
        <thead>
          <tr>
            <th>学员名称</th>
            <th>手机号</th>
            <th>邮箱</th>
            <th>会员类型</th>
            <th>到期日期</th>
            <th>预约课程数</th>
            <th>状态</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="member in filteredMembers" :key="member.memberId">
            <td><span class="member-name">{{ member.name }}</span></td>
            <td>{{ member.phone }}</td>
            <td>{{ member.email }}</td>
            <td><span class="badge badge-type">{{ member.memberTypeLabel }}</span></td>
            <td>{{ formatDate(member.expireDate) }}</td>
            <td><span class="badge badge-count">{{ member.appointmentCount || 0 }}</span></td>
            <td>
              <span class="status-badge" :class="member.status === 1 ? 'active' : 'inactive'">
                {{ member.status === 1 ? '活跃' : '已离职' }}
              </span>
            </td>
            <td>
              <div class="actions">
                <button class="act-btn view" @click="viewMember(member)" title="查看详情"><el-icon><View /></el-icon></button>
              </div>
            </td>
          </tr>
          <tr v-if="filteredMembers.length === 0">
            <td colspan="8" class="empty-state">暂无学员数据</td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>

    <el-dialog v-model="showDetail" title="学员详情" width="600px">
      <div v-if="selectedMember" class="member-detail">
        <div class="detail-row">
          <span class="label">姓名：</span>
          <span class="value">{{ selectedMember.name }}</span>
        </div>
        <div class="detail-row">
          <span class="label">手机号：</span>
          <span class="value">{{ selectedMember.phone }}</span>
        </div>
        <div class="detail-row">
          <span class="label">邮箱：</span>
          <span class="value">{{ selectedMember.email }}</span>
        </div>
        <div class="detail-row">
          <span class="label">会员类型：</span>
          <span class="value">{{ selectedMember.memberTypeLabel }}</span>
        </div>
        <div class="detail-row">
          <span class="label">到期日期：</span>
          <span class="value">{{ formatDate(selectedMember.expireDate) }}</span>
        </div>
        <div class="detail-row">
          <span class="label">预约课程数：</span>
          <span class="value">{{ selectedMember.appointmentCount || 0 }}</span>
        </div>
        <div class="detail-row">
          <span class="label">状态：</span>
          <span class="value">
            <span class="status-badge" :class="selectedMember.status === 1 ? 'active' : 'inactive'">
              {{ selectedMember.status === 1 ? '活跃' : '已离职' }}
            </span>
          </span>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { Search, View, UserFilled, Calendar, TrendCharts, Trophy } from '@element-plus/icons-vue'
import api from '@/api/request'

export default {
  name: 'CoachMembersView',
  components: { Search, View, UserFilled, Calendar, TrendCharts, Trophy },
  data() {
    return {
      searchQuery: '',
      filterStatus: '',
      showDetail: false,
      selectedMember: null,
      members: [],
      stats: [
        { label: '总学员数', value: '-', icon: 'UserFilled', bg: 'rgba(0,98,255,0.1)', color: '#0062FF' },
        { label: '活跃学员', value: '-', icon: 'TrendCharts', bg: 'rgba(16,185,129,0.1)', color: '#10B981' },
        { label: '即将到期', value: '-', icon: 'Calendar', bg: 'rgba(245,158,11,0.1)', color: '#F59E0B' },
        { label: '平均评分', value: '-', icon: 'Trophy', bg: 'rgba(139,92,246,0.1)', color: '#8B5CF6' },
      ],
    }
  },
  computed: {
    filteredMembers() {
      let result = this.members
      if (this.searchQuery) {
        result = result.filter(m =>
          m.name.includes(this.searchQuery) ||
          m.phone.includes(this.searchQuery)
        )
      }
      if (this.filterStatus !== '') {
        result = result.filter(m => m.status === this.filterStatus)
      }
      return result
    },
  },
  mounted() {
    this.loadMembers()
  },
  methods: {
    async loadMembers() {
      try {
        const res = await api.getCoachMembers()
        if (res.data && res.data.code === 200) {
          const list = res.data.data || []
          const memberTypes = { '月卡': '月卡', '季卡': '季卡', '年卡': '年卡', '私教': '私教' }
          this.members = list.map(m => ({
            ...m,
            memberTypeLabel: memberTypes[m.memberType] || m.memberType,
            appointmentCount: m.appointmentCount || 0,
          }))

          const active = list.filter(m => m.status === 1).length
          const expiring = list.filter(m => {
            const expireDate = new Date(m.expireDate)
            const today = new Date()
            const daysLeft = Math.ceil((expireDate - today) / (1000 * 60 * 60 * 24))
            return daysLeft > 0 && daysLeft <= 30
          }).length
          const avgRating = list.length ? (list.reduce((s, m) => s + (m.rating || 0), 0) / list.length).toFixed(1) : '-'

          this.stats[0].value = String(list.length)
          this.stats[1].value = String(active)
          this.stats[2].value = String(expiring)
          this.stats[3].value = avgRating
        }
      } catch (e) {
        this.$message.error('加载学员数据失败')
      }
    },
    viewMember(member) {
      this.selectedMember = member
      this.showDetail = true
    },
    formatDate(date) {
      if (!date) return '-'
      const d = new Date(date)
      return d.toLocaleDateString('zh-CN')
    },
  },
}
</script>

<style scoped>
.page-wrapper { padding: 0; background: var(--bg-base); min-height: 100vh; }
.page-header { margin-bottom: 24px; }
.page-title { font-size: 22px; font-weight: 700; color: var(--text-primary); margin: 0 0 4px; }
.breadcrumb { font-size: 13px; color: var(--text-muted); display: flex; gap: 6px; }
.breadcrumb .sep { color: var(--border-color); }
.breadcrumb .cur { color: var(--primary-blue); font-weight: 500; }
.mini-stat { background: var(--bg-white); border: 1px solid var(--border-color); border-radius: var(--radius-lg); padding: 16px 20px; display: flex; align-items: center; gap: 14px; transition: all 0.3s; }
.mini-stat:hover { transform: translateY(-2px); box-shadow: var(--shadow-lg); }
.mini-stat-icon { width: 44px; height: 44px; border-radius: var(--radius-md); display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0; }
.mini-stat-val { font-size: 22px; font-weight: 700; color: var(--text-primary); margin: 0 0 2px; font-family: 'Inter', sans-serif; }
.mini-stat-label { font-size: 12px; color: var(--text-muted); margin: 0; }
.filter-bar { display: flex; gap: 12px; margin-bottom: 20px; align-items: center; }
.search-box { display: flex; align-items: center; background: var(--bg-white); border: 1px solid var(--border-color); border-radius: var(--radius-md); padding: 0 12px; height: 38px; flex: 1; max-width: 300px; gap: 8px; transition: all 0.3s; }
.search-box:focus-within { border-color: var(--primary-blue); box-shadow: 0 0 0 3px rgba(0,98,255,0.08); }
.search-box .el-icon { color: var(--text-muted); }
.search-box input { border: none; background: transparent; color: var(--text-primary); font-size: 14px; outline: none; flex: 1; }
.search-box input::placeholder { color: var(--text-muted); }
.filter-group { display: flex; gap: 12px; }
.members-table { background: var(--bg-white); border: 1px solid var(--border-color); border-radius: var(--radius-lg); overflow: hidden; }
.table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.members-table table { width: 100%; border-collapse: collapse; min-width: 640px; }
.members-table thead { background: var(--bg-gray); }
.members-table th { padding: 12px 16px; text-align: left; font-size: 13px; font-weight: 600; color: var(--text-secondary); border-bottom: 1px solid var(--border-color); }
.members-table td { padding: 14px 16px; font-size: 13px; color: var(--text-primary); border-bottom: 1px solid var(--border-color); }
.members-table tbody tr:hover { background: rgba(0, 98, 255, 0.02); }
.member-name { font-weight: 600; color: var(--text-primary); }
.badge { display: inline-block; padding: 4px 10px; border-radius: 12px; font-size: 12px; font-weight: 600; }
.badge-type { background: rgba(0, 98, 255, 0.1); color: var(--primary-blue); }
.badge-count { background: rgba(16, 185, 129, 0.1); color: #10B981; }
.status-badge { display: inline-block; padding: 4px 10px; border-radius: 12px; font-size: 12px; font-weight: 600; }
.status-badge.active { background: rgba(16, 185, 129, 0.1); color: #10B981; }
.status-badge.inactive { background: rgba(245, 158, 11, 0.1); color: #F59E0B; }
.actions { display: flex; gap: 6px; }
.act-btn { width: 32px; height: 32px; border: none; border-radius: var(--radius-sm); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.3s; font-size: 14px; }
.act-btn.view { background: rgba(0, 98, 255, 0.1); color: var(--primary-blue); }
.act-btn.view:hover { background: var(--primary-blue); color: white; }
.empty-state { text-align: center; color: var(--text-muted); padding: 40px 16px !important; }
.member-detail { display: flex; flex-direction: column; gap: 16px; }
.detail-row { display: flex; align-items: center; gap: 12px; }
.detail-row .label { font-weight: 600; color: var(--text-secondary); min-width: 100px; }
.detail-row .value { color: var(--text-primary); flex: 1; }

@media (max-width: 768px) {
  .filter-bar { flex-wrap: wrap; }
  .search-box { max-width: none; flex: 1 1 100%; }
  .filter-group { width: 100%; }
  .filter-group :deep(.el-select) { width: 100% !important; }
}

@media (max-width: 480px) {
  .mini-stat { padding: 12px 14px; }
  .mini-stat-val { font-size: 18px; }
  .detail-row { flex-direction: column; align-items: flex-start; gap: 4px; }
  .detail-row .label { min-width: unset; }
}
</style>

